import type { UrlObject } from 'url'
import { i18n } from '../../next-i18next.config'

const normalizeLocale = (locale?: string): string => {
  if (!locale) return 'ja'
  const normalized = locale.toLowerCase().replace('_', '-')
  if (normalized === 'default') return 'ja'
  const matched = i18n.locales.find((l: string) => l.toLowerCase() === normalized)
  return matched || normalized
}

const hasLocalePrefix = (pathname: string): boolean => {
  const lowerPath = pathname.toLowerCase()
  return i18n.locales.some((locale: string) => {
    const lowerLocale = locale.toLowerCase()
    return lowerPath === `/${lowerLocale}` || lowerPath.startsWith(`/${lowerLocale}/`)
  })
}

const shouldSkipPathname = (pathname: string): boolean => {
  return pathname.startsWith('/api') || pathname.startsWith('/_next')
}

const buildPrefixedPath = (pathname: string, locale?: string): string => {
  if (!pathname.startsWith('/')) return pathname
  if (shouldSkipPathname(pathname) || hasLocalePrefix(pathname)) {
    return pathname
  }

  const normalizedLocale = normalizeLocale(locale)
  const prefix = `/${normalizedLocale}`

  if (pathname === '/') {
    return prefix
  }

  return `${prefix}${pathname}`
}

export const addLocalePrefix = (href: string, locale?: string): string => {
  if (!href.startsWith('/')) {
    return href
  }

  const base = new URL(href, 'http://_')
  base.pathname = buildPrefixedPath(base.pathname, locale)
  return `${base.pathname}${base.search}${base.hash}`
}

/**
 * Returns true only for the page patterns that require an explicit /ja prefix
 * when the language switcher navigates to Japanese.
 * asPath is expected to be router.asPath (locale-stripped by Next.js).
 */
export const isJaCanonicalTargetPage = (asPath: string): boolean => {
  const pathname = asPath.split('?')[0].split('#')[0]
  if (pathname === '/') return true
  if (/^\/[^/]+\/city(\/|$)/.test(pathname)) return true
  if (['/space/', '/locker/', '/pudo/'].some((p) => pathname.startsWith(p))) return true
  return false
}

export const getLangLinkProps = (slug: string, asPath: string) => {
  if (slug === 'ja' && isJaCanonicalTargetPage(asPath)) {
    return { href: asPath, as: `/ja${asPath}`, locale: 'ja' }
  }
  return { href: asPath, locale: slug }
}

export const withLocalePrefix = (href: string | UrlObject, locale?: string): string | UrlObject => {
  if (typeof href === 'string') {
    return addLocalePrefix(href, locale)
  }

  const cloned: UrlObject = { ...href }
  if (typeof cloned.pathname === 'string') {
    cloned.pathname = buildPrefixedPath(cloned.pathname, locale)
  }

  return cloned
}
